import { useState } from 'react';

import {
  Box,
  Card,
  Chip,
  Divider,
  Link,
  Stack,
  Typography,
  Button as MuiButton,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

import { Button } from '@karehero/llama/components/atoms/Button/Button';
import { PageHeader } from '@karehero/llama/pwa/atoms/PageHeader/PageHeader';
import { radius } from '@karehero/llama/theme/radius';

import StatusBar from '@/designs-dev/device/StatusBar';
import BottomSheet from '@/designs-dev/device/BottomSheet';
import { statusChipColor } from '@/karehero-app/care/shortlists/ProviderCard';
import { type ShortlistProvider } from '@/karehero-app/care/shortlists/fixtures';
import ScreenRoot from '@/components/wrapped/ScreenRoot';

// Frosted (translucent + blur) background for the sticky footer CTA.
const frostedFooter = {
  backgroundColor: (theme: any) =>
    alpha(theme.palette.background.default, 0.72),
  backdropFilter: 'blur(10px)',
  WebkitBackdropFilter: 'blur(10px)',
};

const ratingColor = (r: string) =>
  r === 'Outstanding' ? 'success' : r === 'Good' ? 'limeGreen' : 'warmOrange';

const Stat = ({ label, value }: { label: string; value: string }) => (
  <Box>
    <Typography variant='caption' color='text.secondary'>
      {label}
    </Typography>
    <Typography variant='body1'>{value}</Typography>
  </Box>
);

const Section = ({
  title,
  subheading,
  chip,
  children,
}: {
  title: string;
  // Sits under the title, e.g. 'CQC overall · last inspected Jan 2026'.
  subheading?: string;
  // Sits on the heading line, right-aligned (e.g. the CQC rating chip).
  chip?: React.ReactNode;
  children: React.ReactNode;
}) => (
  <Card sx={{ p: 2 }}>
    <Stack spacing={1.5}>
      <Stack
        sx={{ alignItems: 'flex-start', justifyContent: 'space-between' }}
        direction='row'
        spacing={1}
      >
        <Stack sx={{ minWidth: 0 }} spacing={0.25}>
          <Typography variant='h4'>{title}</Typography>
          {subheading && (
            <Typography variant='subtitle2' color='text.secondary'>
              {subheading}
            </Typography>
          )}
        </Stack>
        {chip}
      </Stack>
      {children}
    </Stack>
  </Card>
);

// Provider detail, care-circle side: full parity with the admin detail view
// minus admin controls — photos, stats, contact (tappable phone), description,
// care types, services and specialisms, features, owner's quote, pricing and
// funding, regulator rating breakdown. The CAM's note and status show here
// too, consistent with the card.
const ProviderDetail = ({
  provider,
  onBack,
  onToggleSecured,
  initialBooking = false,
  asSheet = false,
}: {
  provider: ShortlistProvider;
  onBack: () => void;
  // Family marks the home secured (or clears it) from the drawer footer, where
  // it's the primary action. Omitted in read-only contexts — then only Book a
  // visit shows.
  onToggleSecured?: (provider: ShortlistProvider) => void;
  // Harness-only: render with the book-a-visit sheet already open.
  initialBooking?: boolean;
  // Care-circle side: open as a bottom sheet over the shortlist instead of a
  // full pushed page. The sheet owns the close affordance and footer.
  asSheet?: boolean;
}) => {
  const [booking, setBooking] = useState(initialBooking);
  // No point booking a visit somewhere with no beds.
  const canBook = provider.status !== 'Unavailable';
  const secured = provider.status === 'Secured';
  // Securing is offered when the caller wires it and the home is available.
  const canSecure =
    Boolean(onToggleSecured) && provider.status !== 'Unavailable';

  const content = (
    <Stack spacing={2}>
      {/* photos */}
      <Stack spacing={1.5}>
        <Box
          sx={{
            height: 160,
            borderRadius: `${radius.md}px`,
            bgcolor: 'custom.neutral.200',
          }}
        />
        <Stack direction='row' spacing={1.5}>
          {[0, 1, 2].map((i) => (
            <Box
              sx={{
                flex: 1,
                height: 64,
                borderRadius: '8px',
                bgcolor: 'custom.neutral.200',
              }}
              key={i}
            />
          ))}
        </Stack>
      </Stack>

      {/* address + status. In sheet mode the name is the sheet title, so it's
          not repeated here; in page mode the PageHeader carries it. */}
      <Box>
        {asSheet ? (
          <Box sx={{ px: 1, mb: 1 }}>
            <Typography variant='body1' color='text.secondary'>
              {`${provider.address} · ${provider.distance}`}
            </Typography>
          </Box>
        ) : (
          <PageHeader
            variant='h2'
            title={provider.name}
            description={`${provider.address} · ${provider.distance}`}
          />
        )}
        <Box sx={{ px: 1 }}>
          <Chip
            size='small'
            label={provider.status}
            color={statusChipColor(provider.status)}
          />
        </Box>
      </Box>

      {/* CAM note */}
      {provider.note && (
        <Box
          sx={{ bgcolor: 'custom.neutral.100', borderRadius: '8px', p: 1.5 }}
        >
          <Stack spacing={0.5}>
            <Typography variant='subtitle2' color='text.secondary'>
              Note from your care expert
            </Typography>
            <Typography variant='body1'>{provider.note}</Typography>
          </Stack>
        </Box>
      )}

      {/* at a glance */}
      <Card sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Box
            sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}
          >
            <Stat label='Regulator rating' value={provider.rating} />
            <Stat label='Price' value={provider.price} />
            <Stat label='Beds' value={String(provider.beds)} />
            <Stat label='Availability' value={provider.availability} />
            <Stat label='Manager' value={provider.manager} />
            <Stat label='Visiting' value={provider.visiting} />
          </Box>

          <Divider />

          <Stack spacing={0.5}>
            <Typography variant='subtitle2'>Contact</Typography>
            {/* click-to-call per the PRD */}
            <Link
              href={`tel:${provider.phone.replace(/\s/g, '')}`}
              variant='body1'
            >
              {provider.phone}
            </Link>
            <Typography variant='body1' color='text.secondary'>
              {provider.website}
            </Typography>
          </Stack>
        </Stack>
      </Card>

      {/* about */}
      <Section title='About'>
        <Typography
          sx={{ fontStyle: 'italic' }}
          variant='body1'
          color='text.secondary'
        >
          {provider.ownerQuote}
        </Typography>
        <Typography variant='body1' color='text.secondary'>
          {provider.description}
        </Typography>
      </Section>

      {/* types of care */}
      <Section title='Types of care'>
        <Stack
          sx={{ flexWrap: 'wrap' }}
          direction='row'
          spacing={1.5}
          useFlexGap
        >
          {provider.careTypes.map((c) => (
            <Chip key={c} label={c} size='small' />
          ))}
        </Stack>
      </Section>

      {/* services and specialisms */}
      <Section title='Services and specialisms'>
        <Stack
          sx={{ flexWrap: 'wrap' }}
          direction='row'
          spacing={1.5}
          useFlexGap
        >
          {provider.services.map((s) => (
            <Chip key={s} label={s} size='small' />
          ))}
        </Stack>
      </Section>

      {/* features */}
      <Section title='Features'>
        <Box sx={{ m: 0, pl: 3 }} component='ul'>
          {provider.features.map((f) => (
            <Typography
              key={f}
              component='li'
              variant='body1'
              color='text.secondary'
            >
              {f}
            </Typography>
          ))}
        </Box>
      </Section>

      {/* pricing and funding */}
      <Section title='Pricing and funding'>
        <Typography variant='body1' color='text.secondary'>
          {provider.funding}
        </Typography>
      </Section>

      {/* regulator rating */}
      <Section
        title='Regulator rating'
        subheading={`CQC overall · last inspected ${provider.lastInspected}`}
        chip={
          <Chip
            size='small'
            label={provider.rating}
            color={ratingColor(provider.rating) as never}
          />
        }
      >
        <Stack spacing={1}>
          {provider.inspection.map((a) => (
            <Stack
              sx={{ justifyContent: 'space-between', alignItems: 'center' }}
              key={a.area}
              direction='row'
            >
              <Typography variant='body1'>{a.area}</Typography>
              <Chip
                label={a.rating}
                size='small'
                color={ratingColor(a.rating) as never}
              />
            </Stack>
          ))}
        </Stack>
      </Section>
    </Stack>
  );

  // Mark as secured — the drawer's primary action when the caller wires it.
  const secureButton = canSecure ? (
    <Button
      variant='primary'
      size='lg'
      isFullWidth
      ariaLabel={secured ? 'Mark as not secured' : 'Mark as secured'}
      onPress={() => onToggleSecured!(provider)}
    >
      {secured ? 'Mark as not secured' : 'Mark as secured'}
    </Button>
  ) : null;

  // Book a visit drops to secondary when securing is the primary action.
  const bookButton = canBook ? (
    <Button
      variant={secureButton ? 'secondary' : 'primary'}
      size='lg'
      isFullWidth
      ariaLabel='Book a visit'
      onPress={() => setBooking(true)}
    >
      Book a visit
    </Button>
  ) : null;

  // Footer actions, stacked: secure (primary) above book (secondary), 16px apart.
  const footerActions =
    secureButton || bookButton ? (
      <Stack spacing={2}>
        {secureButton}
        {bookButton}
      </Stack>
    ) : null;

  // Call / email options, opened from "Book a visit". Portals out, so it sits
  // correctly whether the detail is a page or a sheet.
  const bookingSheet = booking ? (
    <BottomSheet variant='hug' onClose={() => setBooking(false)}>
      <Stack sx={{ pb: 1 }} spacing={1.5}>
        <MuiButton
          fullWidth
          size='large'
          variant='outlined'
          color='primary'
          component='a'
          href={`tel:${provider.phone.replace(/\s/g, '')}`}
        >
          Call {provider.phone}
        </MuiButton>
        <MuiButton
          fullWidth
          size='large'
          variant='outlined'
          color='primary'
          component='a'
          href={`mailto:${provider.email}`}
        >
          Email them
        </MuiButton>
      </Stack>
    </BottomSheet>
  ) : null;

  if (asSheet) {
    // The booking sheet after the provider's rather than inside it: both
    // portal to the screen, and the inner one landed first, under it.
    return (
      <>
        <BottomSheet
          variant='full'
          title={provider.name}
          onClose={onBack}
          footer={footerActions ?? undefined}
        >
          {content}
        </BottomSheet>
        {bookingSheet}
      </>
    );
  }

  return (
    <ScreenRoot relative>
      <StatusBar />

      <Box
        sx={{
          px: 2,
          py: 0.5,
          flexShrink: 0,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Button
          variant='link'
          ariaLabel='Go back'
          iconLeft='chevron-left'
          onPress={onBack}
        >
          Back
        </Button>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          px: 2,
          pt: 2,
          pb: canBook ? '96px' : 3,
        }}
      >
        {content}
      </Box>

      {/* primary action: book a visit */}
      {canBook && (
        <Box
          sx={[
            {
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 0,
              px: 2,
              py: 1.5,
            },
            frostedFooter,
          ]}
        >
          {bookButton}
        </Box>
      )}

      {bookingSheet}
    </ScreenRoot>
  );
};

export default ProviderDetail;
